import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SimpleDropdown from '~/modules/common/components/SimpleDropdown';
import {
  ResourceRequestsToRescheduleByStatus,
  ResourceRequestsToRescheduleByStatus2
} from '~/types';

const labelId = 'reschedule-resource-requests-label';

const selectProps = {
  id: 'reschedule-resource-requests',
  labelId,
  SelectDisplayProps: {
    role: 'combobox'
  }
};

const inputLabelProps = {
  id: labelId
};

const useStyles = makeStyles(theme => ({
  collapse: {
    margin: theme.spacing(0, 0, 0, 3.5),
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0
    }
  }
}));

export const ResourceRequestsToRescheduleDropDown = ({
  value,
  onChange,
  resourceRequestsStatusCountSummary,
  isRescheduleResourceRequestsChecked,
  isPsaRmpTaskAllocation1Enabled
}) => {
  const intl = useIntl();
  const classes = useStyles();

  const { completed, proposed } = resourceRequestsStatusCountSummary;

  const isCompletedOrProposedRequestsPresent = completed > 0 || proposed > 0;

  const options = useMemo(
    () =>
      [
        proposed > 0
          ? {
              id:
                ResourceRequestsToRescheduleByStatus.WithoutAcceptedResourcesRequests,
              displayText: intl.formatMessage({
                id:
                  'rescheduleProject.rescheduleWithoutAcceptedResourceRequests'
              })
            }
          : undefined,
        {
          id:
            ResourceRequestsToRescheduleByStatus.WithoutProposedOrAcceptedResourcesRequests,
          displayText: intl.formatMessage({
            id:
              'rescheduleProject.rescheduleWithoutProposedOrAcceptedResourceRequests'
          })
        },
        completed > 0
          ? {
              id:
                ResourceRequestsToRescheduleByStatus.ReleaseAndRescheduleAllRequests,
              displayText: intl.formatMessage({
                id: 'rescheduleProject.releaseAndRescheduleAllRequests'
              })
            }
          : undefined
      ].filter(x => x),
    [intl, proposed, completed]
  );

  const rescheduleResourceRequestOptions = useMemo(
    () => [
      {
        id: ResourceRequestsToRescheduleByStatus2.AllResourceRequests,
        displayText: intl.formatMessage({
          id: 'rescheduleProject.rescheduleAllResourceRequests'
        })
      },
      {
        id:
          ResourceRequestsToRescheduleByStatus2.WithoutProposedResourceRequests,
        displayText: intl.formatMessage({
          id: 'rescheduleProject.rescheduleWithoutProposedResourceRequests'
        })
      }
    ],
    [intl]
  );

  const selectedValue = useMemo(() => {
    const availableOptions = isPsaRmpTaskAllocation1Enabled
      ? rescheduleResourceRequestOptions
      : options;

    return availableOptions.find(x => x.id === value);
  }, [
    isPsaRmpTaskAllocation1Enabled,
    rescheduleResourceRequestOptions,
    options,
    value
  ]);

  const collapseClasses = useMemo(() => ({ container: classes.collapse }), [
    classes.collapse
  ]);

  return isPsaRmpTaskAllocation1Enabled && proposed > 0 ? (
    <Collapse
      in={isRescheduleResourceRequestsChecked}
      timeout="auto"
      classes={collapseClasses}
      unmountOnExit
    >
      <SimpleDropdown
        label={intl.formatMessage({
          id: `rescheduleProject.resourceRequestsToReschedule`
        })}
        variant="outlined"
        margin="dense"
        options={rescheduleResourceRequestOptions}
        onChange={onChange}
        value={selectedValue}
        noneOption={false}
        disableSort
        required
        selectProps={selectProps}
        inputLabelProps={inputLabelProps}
      />
    </Collapse>
  ) : !isPsaRmpTaskAllocation1Enabled &&
    isCompletedOrProposedRequestsPresent ? (
    <Collapse
      in={isRescheduleResourceRequestsChecked}
      timeout="auto"
      classes={collapseClasses}
      unmountOnExit
    >
      <SimpleDropdown
        label={intl.formatMessage({
          id: `rescheduleProject.resourceRequestsToReschedule`
        })}
        variant="filled"
        margin="dense"
        options={options}
        onChange={onChange}
        value={selectedValue}
        noneOption={false}
        disableSort
        required
      />
    </Collapse>
  ) : null;
};

ResourceRequestsToRescheduleDropDown.propTypes = {
  value: PropTypes.oneOf([
    ResourceRequestsToRescheduleByStatus.ReleaseAndRescheduleAllRequests,
    ResourceRequestsToRescheduleByStatus.WithoutAcceptedResourcesRequests,
    ResourceRequestsToRescheduleByStatus.WithoutProposedOrAcceptedResourcesRequests,
    ResourceRequestsToRescheduleByStatus2.WithoutProposedResourceRequests,
    ResourceRequestsToRescheduleByStatus2.AllResourceRequests
  ]),
  onChange: PropTypes.func.isRequired,
  resourceRequestsStatusCountSummary: PropTypes.object,
  isRescheduleResourceRequestsChecked: PropTypes.bool,
  isPsaRmpTaskAllocation1Enabled: PropTypes.bool
};

export default ResourceRequestsToRescheduleDropDown;
